/**
 * Workspace persistence — remembers which business workspace (Candidate vs a Company) the account
 * used last, and carries a pending invitation token across the register → verify → login journey.
 *
 * Keys are scoped per user id so shared browsers never leak one account's workspace choice into
 * another's session. Storage failures (private mode, disabled storage) degrade silently — the app
 * falls back to its default workspace resolution, never crashes.
 */

export type WorkspaceKind = 'candidate' | 'company';

export interface LastWorkspace {
  kind: WorkspaceKind;
  /** Set when `kind === 'company'` — which company was active. */
  companyId: string | null;
}

const lastWorkspaceKey = (userId: string) => `willnq.workspace.last.${userId}`;

/** The pending-invitation token is per browser tab (sessionStorage): the invited user registers,
 * verifies and signs in within one tab, and the root redirect returns them to the landing page. */
const PENDING_INVITATION_KEY = 'willnq.invitation.pendingToken';

export function readLastWorkspace(userId: string): LastWorkspace | null {
  try {
    const raw = window.localStorage.getItem(lastWorkspaceKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastWorkspace;
    if (parsed.kind !== 'candidate' && parsed.kind !== 'company') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeLastWorkspace(userId: string, workspace: LastWorkspace): void {
  try {
    window.localStorage.setItem(lastWorkspaceKey(userId), JSON.stringify(workspace));
  } catch {
    // Persistence is a convenience — never fail the interaction over it.
  }
}

export function readPendingInvitationToken(): string | null {
  try {
    return window.sessionStorage.getItem(PENDING_INVITATION_KEY);
  } catch {
    return null;
  }
}

export function writePendingInvitationToken(token: string): void {
  try {
    window.sessionStorage.setItem(PENDING_INVITATION_KEY, token);
  } catch {
    // Best-effort — the user can still follow the email link again.
  }
}

export function clearPendingInvitationToken(): void {
  try {
    window.sessionStorage.removeItem(PENDING_INVITATION_KEY);
  } catch {
    // Ignore — an orphaned token is harmless (single generic message on reuse).
  }
}
